import React, { useCallback, useEffect, useRef, useState } from 'react';
import { joinString } from '../../../../_helpers/join-string.ts';

import './Modal.scss';
import { getKeys } from '../../../../_helpers/keyboard.ts';

export enum ModalSize {
	EXTRA_SMALL = 'blk-ac-modal-extra-small',
	SMALL = 'blk-ac-modal-small',
	MEDIUM = 'blk-ac-modal-medium',
	LARGE = 'blk-ac-modal-large',
}

export type ModalProp = {
	modalSize?: ModalSize;
	className?: string;
	fullScreenOnMobile?: boolean;
	ariaLabelDialog: string;
	closeButton?: boolean;
	ariaLabelCloseButton?: string;
	closeCallback: () => void;
	children?: React.ReactNode;
};

const Modal: React.FC<ModalProp> = ({
	modalSize,
	className = '',
	fullScreenOnMobile,
	ariaLabelDialog,
	closeButton = true,
	ariaLabelCloseButton = 'Close Button',
	closeCallback,
	children,
}) => {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [keyboardTouched, setKeyboardTouched] = useState(false);

	useEffect(() => {
		const dialogParentWindow = dialogRef.current?.ownerDocument.defaultView;

		const handleWindowKeyDown = (ev: KeyboardEvent) => {
			setKeyboardTouched(true);
			if (closeButton && ev.key === 'Escape') {
				closeCallback();
				ev.preventDefault();
			}
		};

		dialogParentWindow?.addEventListener('keydown', handleWindowKeyDown);
		dialogRef.current?.showModal();

		return () => {
			dialogParentWindow?.removeEventListener('keydown', handleWindowKeyDown);
			dialogRef.current?.close();
		};
	}, []);

	const handleClickOnDialog = useCallback(
		(event: React.MouseEvent<HTMLDialogElement, MouseEvent>) => {
			if (dialogRef.current === event?.target) {
				closeCallback();
			}
		},
		[closeCallback]
	);

	const handleEscapeKey = (ev: React.KeyboardEvent) => {
		if (!closeButton && getKeys(ev).isEscape) {
			ev.preventDefault();
		}
	};
	return (
		<dialog
			ref={dialogRef}
			className={joinString([
				'blk-ac-modal-dialog',
				fullScreenOnMobile ? 'full-screen-on-mobile' : '',
				keyboardTouched ? '' : 'keyboard-untouched',
				modalSize || '',
				className,
			])}
			onKeyDown={handleEscapeKey}
			aria-label={ariaLabelDialog}
			onClick={(ev) => handleClickOnDialog(ev)}
		>
			{children}

			{closeButton && (
				<button
					className="blk-ac-modal-close-button"
					aria-label={ariaLabelCloseButton}
					tabIndex={0}
					onClick={closeCallback}
				>
					<span className="fa fa-times"></span>
				</button>
			)}
		</dialog>
	);
};

export default Modal;
